// useAdminConfig Hook
// ─────────────────────────────────────────────────────────────
// Manages admin configuration / API keys from the admin panel.
// Reads, saves, and tests integrations via Cloud Functions.

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

// ── Types ───────────────────────────────────────────────────

export interface IntegrationConfig {
  [key: string]: any;
  updatedAt?: any;
  updatedBy?: string;
}

export interface AdminConfig {
  firebase_messaging?: IntegrationConfig;
  sendgrid?: IntegrationConfig;
  twilio?: IntegrationConfig;
  klaviyo?: IntegrationConfig;
  uber_direct?: IntegrationConfig;
  stripe_platform?: IntegrationConfig;
  algolia?: IntegrationConfig;
  general?: IntegrationConfig;
}

export type IntegrationSection = keyof AdminConfig;

export interface IntegrationStatus {
  configured: boolean;
  testing: boolean;
  testResult?: { success: boolean; message: string };
}

// ── Section metadata ────────────────────────────────────────

export interface IntegrationMeta {
  id: IntegrationSection;
  name: string;
  description: string;
  icon: string; // Lucide icon name
  color: string;
  docsUrl: string;
  fields: IntegrationField[];
}

export interface IntegrationField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'email' | 'url';
  placeholder: string;
  required: boolean;
  helpText?: string;
}

export const INTEGRATIONS: IntegrationMeta[] = [
  {
    id: 'firebase_messaging',
    name: 'Firebase Cloud Messaging',
    description: 'Push notifications via FCM (VAPID key for web push)',
    icon: 'Bell',
    color: 'amber',
    docsUrl: 'https://console.firebase.google.com/project/fash-70b87/settings/cloudmessaging',
    fields: [
      {
        key: 'vapid_key',
        label: 'VAPID Key (Web Push Certificate)',
        type: 'password',
        placeholder: 'BPe7...',
        required: true,
        helpText: 'Firebase Console → Project Settings → Cloud Messaging → Web Push certificates → Generate key pair',
      },
    ],
  },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    description: 'Email notifications, receipts, and transactional emails',
    icon: 'Mail',
    color: 'blue',
    docsUrl: 'https://app.sendgrid.com/settings/api_keys',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        placeholder: 'SG.xxxx...',
        required: true,
        helpText: 'SendGrid → Settings → API Keys → Create API Key (Full Access)',
      },
      {
        key: 'from_email',
        label: 'From Email',
        type: 'email',
        placeholder: 'noreply@fashapp.com',
        required: true,
        helpText: 'Must be a verified sender in SendGrid',
      },
      {
        key: 'from_name',
        label: 'From Name',
        type: 'text',
        placeholder: 'Fash',
        required: false,
      },
    ],
  },
  {
    id: 'twilio',
    name: 'Twilio',
    description: 'SMS notifications and alerts',
    icon: 'MessageSquare',
    color: 'red',
    docsUrl: 'https://console.twilio.com',
    fields: [
      {
        key: 'account_sid',
        label: 'Account SID',
        type: 'password',
        placeholder: 'ACxxxx...',
        required: true,
        helpText: 'Twilio Console → Account Info → Account SID',
      },
      {
        key: 'auth_token',
        label: 'Auth Token',
        type: 'password',
        placeholder: 'xxxx...',
        required: true,
        helpText: 'Twilio Console → Account Info → Auth Token',
      },
      {
        key: 'from_number',
        label: 'From Phone Number',
        type: 'text',
        placeholder: '+1234567890',
        required: true,
        helpText: 'Must be a Twilio phone number you own',
      },
    ],
  },
  {
    id: 'klaviyo',
    name: 'Klaviyo',
    description: 'Email marketing, profile sync, and lifecycle event tracking',
    icon: 'Megaphone',
    color: 'green',
    docsUrl: 'https://www.klaviyo.com/settings/account/api-keys',
    fields: [
      {
        key: 'private_api_key',
        label: 'Private API Key',
        type: 'password',
        placeholder: 'pk_xxxx...',
        required: true,
        helpText: 'Klaviyo → Settings → API Keys → Create Private Key (profiles:write, events:write, lists:write scopes)',
      },
      {
        key: 'list_id',
        label: 'Default List ID',
        type: 'text',
        placeholder: 'XXXXXX',
        required: false,
        helpText: 'Optional — Klaviyo list ID for marketing subscribers (find in Lists & Segments → Settings)',
      },
    ],
  },
  {
    id: 'uber_direct',
    name: 'Uber Direct',
    description: 'Same-day courier delivery integration',
    icon: 'Truck',
    color: 'gray',
    docsUrl: 'https://developer.uber.com/dashboard',
    fields: [
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'password',
        placeholder: 'xxxx...',
        required: true,
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        placeholder: 'xxxx...',
        required: true,
      },
      {
        key: 'customer_id',
        label: 'Customer ID',
        type: 'text',
        placeholder: 'xxxx...',
        required: true,
      },
      {
        key: 'webhook_secret',
        label: 'Webhook Signing Secret',
        type: 'password',
        placeholder: 'xxxx...',
        required: false,
        helpText: 'Used to verify Uber webhook signatures',
      },
    ],
  },
  {
    id: 'stripe_platform',
    name: 'Stripe (Platform)',
    description: 'Platform-level Stripe keys for the marketplace',
    icon: 'CreditCard',
    color: 'purple',
    docsUrl: 'https://dashboard.stripe.com/apikeys',
    fields: [
      {
        key: 'secret_key',
        label: 'Secret Key',
        type: 'password',
        placeholder: 'sk_live_xxxx...',
        required: true,
        helpText: 'Platform-level secret key (individual boutiques connect separately)',
      },
      {
        key: 'publishable_key',
        label: 'Publishable Key',
        type: 'text',
        placeholder: 'pk_live_xxxx...',
        required: true,
      },
      {
        key: 'webhook_secret',
        label: 'Connect Webhook Signing Secret',
        type: 'password',
        placeholder: 'whsec_xxxx...',
        required: false,
        helpText: 'Signing secret for the stripeConnectWebhook endpoint (account.updated)',
      },
      {
        key: 'payment_webhook_secret',
        label: 'Payments Webhook Signing Secret',
        type: 'password',
        placeholder: 'whsec_xxxx...',
        required: false,
        helpText: 'Signing secret for the stripePaymentWebhook endpoint (payment_intent.succeeded, checkout.session.completed)',
      },
    ],
  },
  {
    id: 'algolia',
    name: 'Algolia',
    description: 'Product search and discovery',
    icon: 'Search',
    color: 'indigo',
    docsUrl: 'https://dashboard.algolia.com/account/api-keys',
    fields: [
      {
        key: 'app_id',
        label: 'Application ID',
        type: 'text',
        placeholder: 'XXXX...',
        required: true,
      },
      {
        key: 'admin_api_key',
        label: 'Admin API Key',
        type: 'password',
        placeholder: 'xxxx...',
        required: true,
        helpText: 'Used server-side for indexing. Never expose to the frontend.',
      },
      {
        key: 'search_api_key',
        label: 'Search-Only API Key',
        type: 'password',
        placeholder: 'xxxx...',
        required: true,
        helpText: 'Safe to use on the frontend for search queries',
      },
      {
        key: 'index_name',
        label: 'Products Index Name',
        type: 'text',
        placeholder: 'fash_products',
        required: true,
      },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════
//  HOOK
// ═══════════════════════════════════════════════════════════════

export function useAdminConfig() {
  const [config, setConfig] = useState<AdminConfig>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null); // section being saved
  const [statuses, setStatuses] = useState<Record<string, IntegrationStatus>>({});

  // ── Load config ───────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const getAdminConfigFn = httpsCallable(functions, 'getAdminConfig');
      const result = await getAdminConfigFn({});
      const data = result.data as { config: AdminConfig };
      setConfig(data.config || {});

      // Determine configured status for each integration
      const newStatuses: Record<string, IntegrationStatus> = {};
      for (const integration of INTEGRATIONS) {
        const sectionConfig = (data.config || {})[integration.id] || {};
        const requiredFields = integration.fields.filter((f) => f.required);
        const allConfigured = requiredFields.every(
          (f) => sectionConfig[f.key] || sectionConfig[`${f.key}_set`]
        );
        newStatuses[integration.id] = {
          configured: allConfigured,
          testing: false,
        };
      }
      setStatuses(newStatuses);
    } catch (err: any) {
      console.error('Failed to load admin config:', err);
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // ── Save section ──────────────────────────────────────────
  const saveSection = useCallback(
    async (section: IntegrationSection, sectionConfig: Record<string, any>): Promise<boolean> => {
      try {
        setSaving(section);
        setError(null);

        // Filter out display-only fields (_set suffixes) and empty values
        const cleanConfig: Record<string, any> = {};
        for (const [key, value] of Object.entries(sectionConfig)) {
          if (!key.endsWith('_set') && key !== 'updatedAt' && key !== 'updatedBy') {
            // Only include non-masked values (skip values starting with dots)
            if (typeof value === 'string' && value.startsWith('••••')) {
              continue; // Skip masked values — they haven't changed
            }
            if (value !== undefined && value !== '') {
              cleanConfig[key] = value;
            }
          }
        }

        const saveAdminConfigFn = httpsCallable(functions, 'saveAdminConfig');
        await saveAdminConfigFn({ section, config: cleanConfig });

        // Reload to get masked version
        await loadConfig();

        return true;
      } catch (err: any) {
        console.error(`Failed to save ${section} config:`, err);
        setError(err.message || `Failed to save ${section} configuration`);
        return false;
      } finally {
        setSaving(null);
      }
    },
    [loadConfig]
  );

  // ── Test integration ──────────────────────────────────────
  const testIntegration = useCallback(
    async (section: IntegrationSection): Promise<{ success: boolean; message: string }> => {
      setStatuses((prev) => ({
        ...prev,
        [section]: { ...prev[section], testing: true, testResult: undefined },
      }));

      try {
        const testFn = httpsCallable(functions, 'testAdminIntegration');
        const result = await testFn({ section });
        const testResult = result.data as { success: boolean; message: string };

        setStatuses((prev) => ({
          ...prev,
          [section]: { ...prev[section], testing: false, testResult },
        }));

        return testResult;
      } catch (err: any) {
        const testResult = { success: false, message: err.message || 'Test failed' };
        setStatuses((prev) => ({
          ...prev,
          [section]: { ...prev[section], testing: false, testResult },
        }));
        return testResult;
      }
    },
    []
  );

  return {
    config,
    loading,
    error,
    saving,
    statuses,
    saveSection,
    testIntegration,
    reload: loadConfig,
  };
}
